/**
* TextWrap.js
* 
* Created on 16-okt-2014, 9:16:42
*
* @author Harm Boschloo for Ranj
* @author Henrique Campos
**/

import { containsCJKCharacters } from "../../util/StringUtil";

export default class TextWrap {

    /**
     * Wraps words on whitespaces and splits a word when it is too log for a line.
     * 
     * @param {string} text the string to wrap
     * @param {number} maxWidth maximum width of each line in pixels
     * @param {CanvasRenderingContext2D} context the 2D rendering context to determine size of text
     * @param {boolean} returnLines true if should return lines in an Array; false, if it should be combined in one string
     * @returns {string|Array<string>} text with breaklines on wrapping positions, or array with lines
     */
    static wrapText(text, maxWidth, context, returnLines = false) {        
        // first, split lines by breaklines
        var lines = String(text).split(/(?:\r\n|\r|\n)/);
        var newLines = [];
        
        for(let line of lines) {
            var lineWidth = context.measureText(line).width;
            if (lineWidth <= maxWidth) {
                newLines.push(line);
            } else {
                let splitLine = TextWrap._splitLine(line, maxWidth, context);
                newLines.push(...splitLine);
            }
        }
        
        return returnLines ? newLines : TextWrap._combineLines(newLines);
    };

    /**
     * Wraps text from createjs.Text by splitting long words with breaklines 
     * 
     * NB: createjs.Text wrapping only works with whitespaces.
     * 
     * @param {createjs.Text} textField the createjs.Text to wrap
     * @param {number} lineWidth the target line width in pixels; if none is provided it uses the createjs.Text.lineWidth
     * @param {number} maxLines the maximum amount of lines to wrap, if maximum is reached it will truncate the text
     * @param {string} truncateCharacters string to use on truncating if maxLines are provided
     */
    static wrapTextField (textField, lineWidth = undefined, maxLines = 0, truncateCharacters = "...") {
        // NB: if no line width is provided, try the Text lineWidth or the oldLineWidth added by this method
        lineWidth = lineWidth || textField.lineWidth || textField.oldLineWidth;
        if(!lineWidth) {
            console.error("lineWidth or textField.lineWidth not provided");
            return;
        }

        // NB: apply the CanvasRenderingContext2D to the TextField to determine size of text
        /** @type {CanvasRenderingContext2D} */
        var context = textField._prepContext(createjs.Text._workingContext);

        if (maxLines > 0) {
            var lines = TextWrap.wrapText(textField.text, lineWidth, context, true);
            if (lines.length > maxLines) {
                lines = lines.slice(0, maxLines);
                lines[maxLines-1] = lines[maxLines-1].slice(0, -truncateCharacters.length) + truncateCharacters;
            }
            textField.text = TextWrap._combineLines(lines);
        } else {
            textField.text = TextWrap.wrapText(textField.text, lineWidth, context);
        }
        // Stores line width and prevent createjs wrapping
        // NB: sets lineWidth to null so createjs does not uses its auto-wrap
        textField.oldLineWidth = lineWidth;
        textField.lineWidth = null;
    }
    
    /**
     * 
     * @param {string} line 
     * @param {number} maxWidth 
     * @param {CanvasRenderingContext2D} context 
     * @return {Array<string>}
     */
    static _splitLine(line, maxWidth, context) {
        // split words on whitespaces; returns whitespaces as well
        var parts = line.split(/(\s)/);
                
        var splitLines = [];
        var newLine = "";
        for (var j = 0; j < parts.length; j++) {
            var part = parts[j];
            let newLineWidth = context.measureText(newLine + part).width;
            
            if (newLineWidth <= maxWidth) {
                // if text fits, just add it to line
                newLine += part;
            } else if (containsCJKCharacters(part)){
                // if part has CJK characters, break it in any character so it fills the line
                //
                // Limitation: This algorithm does not respect punctuation rules (i.e., lines should not end or start
                // with certain symbols). 
                //
                // See: https://www.w3.org/International/articles/typography/linebreak.en#sec_character
                // See: https://en.wikipedia.org/wiki/Line_breaking_rules_in_East_Asian_languages 
                //
                let splitWord = this._splitWord(newLine + part, maxWidth, context);
                newLine = splitWord[0];
                // add remainder to the parts so it is processed again
                parts.splice(j+1, 0, splitWord[1]);
                splitLines.push(newLine);
                newLine = "";
            } else if (newLine === "") {
                // if part is too big to even fit by itself in one line, split it
                var wordParts = TextWrap._splitWord(part, maxWidth, context);
                // add split word into the parts by replacing its original so the remainder is processed next
                parts.splice(j, 1, ...wordParts);
                // add first part of the word to the lines
                splitLines.push(wordParts[0]);
            } else {
                // if line has reached the width limit, add it to the lines and process again
                splitLines.push(newLine);
                newLine = "";
                j--;
            }
        }
        splitLines.push(newLine);
        return splitLines;
    }

    /**
     * Splits a word in two: a width fitting word and the remainder
     * 
     * @param {string} word 
     * @param {number} maxWidth 
     * @param {CanvasRenderingContext2D} context 
     * @returns {Array<string>} where the first fits the width and the second is the remainder
     */
    static _splitWord (word, maxWidth, context) {
        let firstPart = word;
        let firstPartSize = word.length;
        // iterates through first part by reducing 1 character at a time until it fits
        while(context.measureText(firstPart).width > maxWidth) {
            firstPartSize--;
            firstPart = word.substr(0, firstPartSize);
        }
        // create array where first is the word that fits the width followed by the remainder word
        let wordParts = [firstPart, word.substr(firstPartSize, word.length - firstPartSize)];
        return wordParts;
    }
    
    /**
     * 
     * @param {Array<string>} lines 
     * @returns {string}
     */
    static _combineLines(lines) {
        var text = "";
        for (var i = 0; i < lines.length; ++i) {
            text += ((i === 0 ? "" : "\n") + lines[i]);
        }
        return text;
    }
}